import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Button, Typography, DialogTitle, Dialog, DialogActions, DialogContent,
  Toolbar, TextField, Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableRow, IconButton, Grid,Chip,Autocomplete
} from '@mui/material';
import styled, { keyframes } from 'styled-components';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, PAGE_PADDING, CONTROL_HEIGHT, ELLIPSIS_CSS } from '../../styles/recruiterTheme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchedulingPage from '../Recruiter/SchedulingPage';
import { countryOptions } from '../shared/dropdown_values';
import FeedbackForm from '../Interviewer/FeedBackForm';
import NotificationDialog from './NotificationDialog';
import QuestionDialog from './QuestionDialog';
import CandidateTestResults from './CandidateTestResults';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';

// MUI icons for stats
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import FeedbackIcon from '@mui/icons-material/Feedback';
import DescriptionIcon from '@mui/icons-material/Description';
import QuizIcon from '@mui/icons-material/Quiz';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FlagIcon from '@mui/icons-material/Flag';
import useActivityLogger from '../../hooks/useActivityLogger';


// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page Wrapper ─────────────────────────────────────────────────────────────
// No app bar is rendered above this page (standalone route or inside a dashboard,
// which already offsets its own app bar), so the top gap is just the page padding.
// Inside a dashboard (onBack passed) the dashboard supplies the 32px canvas padding.
const PageWrapper = styled.div`
  font-family: ${FONT_BODY};
  width: 100%;
  box-sizing: border-box;
  ${p => p.$embedded ? '' : `
    background: ${COLORS.background};
    min-height: 100vh;
    padding: ${PAGE_PADDING}px;
  `}
`;

// ─── Header ───────────────────────────────────────────────────────────────────
// Back + title/subtitle on the left (anything extra goes right via HeaderActions).
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const BackBtn = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 36px; height: 36px;
    flex-shrink: 0;
    transition: background 0.2s;
    &:hover { background: linear-gradient(135deg, #E8DEF8, #D0BCFF); }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    ${ELLIPSIS_CSS}
  }
`;

const PageSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  line-height: 1.5;
  font-weight: 500;
  color: ${COLORS.muted};
  min-width: 0;
`;

// ─── Action Strip ─────────────────────────────────────────────────────────────
// White card below the stat row: "Actions" label + a wrapping row of 36px buttons.
const ActionStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  padding: 16px 24px;
  margin-bottom: 24px;
`;

const ActionLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${COLORS.muted};
    line-height: ${CONTROL_HEIGHT.md}px !important;
    margin-right: 8px !important;
  }
`;

// All action buttons share one height so a wrapped row still lines up.
const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${CONTROL_HEIGHT.md}px;
  box-sizing: border-box;
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  line-height: 1;
  border-radius: ${RADIUS.sm}px;
  padding: 0 16px;
  border: none;
  cursor: pointer;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
  white-space: nowrap;

  &.allbuttons-btn, &.accept-btn, &.reject-btn, &.on-hold-btn {
    background: linear-gradient(135deg, #6750A4, #4F378B);
    color: #fff;
    box-shadow: 0 3px 10px rgba(103,80,164,0.28);
    &:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(103,80,164,0.38); }
    &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
  }
`;

// ─── Stat Cards Row ───────────────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.4s ease both;
`;

// Shared box for every block in the stat row → equal size, label on top, value below.
const STAT_BOX = `
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 104px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  position: relative;
  overflow: hidden;
  min-width: 0;
`;

const StatCard = styled.div`
  ${STAT_BOX}
  background: ${p => p.gradient || 'linear-gradient(135deg,#6750A4,#4F378B)'};
  color: #fff;
  box-shadow: 0 5px 18px ${p => p.shadow || 'rgba(103,80,164,0.26)'};
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 10px 24px ${p => p.shadow || 'rgba(103,80,164,0.36)'}; }
  &::after {
    content: '';
    position: absolute;
    top: -22px; right: -22px;
    width: 70px; height: 70px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${p => (p.$text ? TYPE.section : TYPE.stat)}px;
  font-weight: 800;
  line-height: ${p => (p.$text ? 1.25 : 1)};
  font-variant-numeric: tabular-nums;
  max-width: calc(100% - 40px);
  ${ELLIPSIS_CSS}
`;

const StatLbl = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIconWrap = styled.div`
  position: absolute;
  top: 50%; right: 16px;
  transform: translateY(-50%);
  display: flex;
  opacity: 0.2;
  svg { font-size: 36px; }
`;

// Thin match-score bar inside a gradient stat card (white on translucent white).
const ScoreBar = styled.div`
  width: calc(100% - 40px);
  height: 4px;
  border-radius: ${RADIUS.pill}px;
  background: rgba(255,255,255,0.25);
  overflow: hidden;
`;

const ScoreFill = styled.div`
  height: 100%;
  border-radius: ${RADIUS.pill}px;
  background: #fff;
  width: ${p => Math.max(0, Math.min(100, p.val))}%;
  transition: width 0.5s ease;
`;

// ─── Status Chip ──────────────────────────────────────────────────────────────
const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  letter-spacing: 0.06em;
  line-height: 1;
  background: ${p => p.bg};
  color: ${p => p.fg};
  white-space: nowrap;
  max-width: 100%;
`;

const StatusDot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${p => p.color};
  display: inline-block;
  flex-shrink: 0;
`;

// ─── Content Grid ─────────────────────────────────────────────────────────────
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  animation: ${fadeSlideIn} 0.45s ease 0.05s both;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const CARD_BASE = `
  border-radius: ${RADIUS.lg}px !important;
  padding: 24px !important;
  box-shadow: ${SHADOW.card} !important;
  border: 1px solid ${COLORS.border};
  background: #fff !important;
  min-width: 0;
`;

const InfoCard = styled(Paper)`
  && { ${CARD_BASE} }
`;

const SummaryCard = styled(Paper)`
  && { ${CARD_BASE} }
`;

const CardTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    margin-bottom: 16px !important;
    padding-bottom: 12px !important;
    border-bottom: 2px solid #F0EAF9;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

// Label/value grid: every row shares the same label column so values line up.
const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  align-items: baseline;
  column-gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid #FAF7FF;
  &:last-child { border-bottom: none; }
  @media (max-width: 480px) { grid-template-columns: 1fr; row-gap: 4px; }
`;

const InfoKey = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  line-height: 1.5;
  color: ${COLORS.muted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const InfoVal = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 500;
  line-height: 1.5;
  color: ${COLORS.ink};
  overflow-wrap: anywhere;
  min-width: 0;
`;

// ─── Feedback Section ─────────────────────────────────────────────────────────
const FeedbackToggle = styled.button`
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  color: #6750A4;
  background: #EDE7F6;
  border: none;
  border-radius: ${RADIUS.sm}px;
  height: 32px;
  padding: 0 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.18s;
  &:hover { background: #D0BCFF; }
`;

const FeedbackTable = styled(TableContainer)`
  && {
    border-radius: ${RADIUS.md}px !important;
    margin-top: 12px;
    border: 1px solid ${COLORS.border};
    box-shadow: none;
  }
`;

// ─── Summary Bullet ───────────────────────────────────────────────────────────
const BulletItem = styled.li`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 500;
  color: ${COLORS.ink};
  line-height: 1.5;
  margin-bottom: 8px;
  padding-left: 8px;
  list-style: none;
  position: relative;
  &::before {
    content: '➤';
    position: absolute;
    left: -16px;
    top: 2px;
    color: #7C5CBF;
    font-size: ${TYPE.overline}px;
  }
`;

// ─── Dialog Styles ────────────────────────────────────────────────────────────
const DialogTitleStyled = styled(DialogTitle)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.section}px !important;
    line-height: 1.25;
    color: #2D1B5E;
    text-align: left;
    padding: 24px 24px 16px;
  }
`;

const dialogPaperSx = { borderRadius: `${RADIUS.lg}px` };
const dialogContentSx = { padding: '24px !important' };
const dialogActionsSx = {
  padding: '16px 24px', gap: '12px', justifyContent: 'flex-end',
  '& > :not(:first-of-type)': { marginLeft: 0 },
};
const dialogBtnSx = {
  height: 36, px: 2, borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY,
  fontWeight: 600, fontSize: TYPE.small, textTransform: 'none',
};
const cancelBtnSx = { ...dialogBtnSx, color: '#6750A4', border: '1.5px solid #C4B0E8' };
const primaryBtnSx = {
  ...dialogBtnSx, background: 'linear-gradient(135deg,#6750A4,#4F378B)', color: '#fff',
  '&.Mui-disabled': { color: '#fff', opacity: 0.7 },
};
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px` } };
// Header row for dialogs that carry their own close button.
const dialogHeaderSx = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
  padding: '24px 24px 16px', borderBottom: '2px solid #F0EAF9',
};
const dialogHeaderTitleSx = {
  fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: '#2D1B5E',
  display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
};
const closeBtnSx = { width: 36, height: 36, color: '#8B7DB5', borderRadius: `${RADIUS.sm}px` };

// ─── Status helper ────────────────────────────────────────────────────────────
const rejectedStatuses = [
  'ScreeningTest_Failed', 'Recruiter_Rejected', 'ReScreeningTest_Failed',
  'HiringManager_Rejected', 'Rejected', 'Candidate_Declined', 'Candidate_Quit',
  'ReTestPassed_Rejected', 'TestPassed_Rejected', 'Round1_Rejected', 'Interviewer_Rejected'
];
const passedStatuses = ['Selected', 'ScreeningTest_Passed', 'ReScreeningTest_Passed', 'Round2_Suggested'];

const getStatusChip = (status) => {
  if (!status) return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
  if (status === 'NewCandidate')           return { bg: '#FFF8E1', fg: '#E65100', dot: '#FFB300' };
  if (rejectedStatuses.includes(status))  return { bg: '#FDECEA', fg: '#B3261E', dot: '#E53935' };
  if (passedStatuses.includes(status))    return { bg: '#E6F4EA', fg: '#2D7D46', dot: '#2D7D46' };
  return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
};

const formatStatus = (status = "") => {
  return status
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d+)/g, "$1 $2")
    .replace(/\b\w/g, c => c.toUpperCase());
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('rbotUser')) || {};
  } catch {
    return {};
  }
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CandidateData = ({
  interviewid, candidateId: candidateIdProp, userRole: userRoleProp, candidateEmail, candidateName,
  jobDescription, salaryRange, JobRole, companyName, recruiterEmail: recruiterEmailProp,
  overallStatus, jdId, skills, experience, readOnly, onBack
}) => {
  // Route /CandidateData/:candidateId renders this without props — fall back to
  // the URL param and the logged-in user.
  const { candidateId: routeId } = useParams();
  const candidateId = candidateIdProp || routeId;
  const storedUser = getStoredUser();
  const userRole = userRoleProp || storedUser.userType;
  const recruiterEmail = recruiterEmailProp || storedUser.email;

  const [candidateData, setCandidateData] = useState(null);
  const [isLoading, setIsLoading] = useState(!!candidateId);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const loadedIdRef = useRef(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [latestInterviewId, setLatestInterviewId] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [activeTab, setActiveTab] = useState("InterviewerDashboard");
  const [actionTrigger, setActionTrigger] = useState(false);
  const [isActive, setIsActive] = useState(true); // Default to true
const [isToggling, setIsToggling] = useState(false);

  const [editingData, setEditingData] = React.useState({
    rowId: null, candidatename: '', candidateEmail: '', phone: '',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [dialogType, setDialogType] = useState("");
  const navigate = useNavigate();
  const handleBack = () => { if (onBack) onBack(); else navigate(-1); };
  // Mounted inside a dashboard/parent view whenever onBack is passed; the bare route has none.
  const embedded = Boolean(onBack);

  const [statusHistory, setStatusHistory] = useState([]);
const [historyLoading, setHistoryLoading] = useState(false);
const [showHistory, setShowHistory] = useState(false);
const [historyStale, setHistoryStale] = useState(true);
const [questionsLoading, setQuestionsLoading] = useState(false);
const [resumeLoading, setResumeLoading] = useState(false);
const [editError, setEditError] = useState('');

const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
const [rescheduleDate, setRescheduleDate] = useState('');
const [rescheduleTime, setRescheduleTime] = useState('');
const [rescheduleInterviewers, setRescheduleInterviewers] = useState([]);
const [rescheduleReason, setRescheduleReason] = useState('');
const [rescheduling, setRescheduling] = useState(false);
const [latestInterview, setLatestInterview] = useState(null);
// Interviewer view: the latest interview this interviewer is on (falls back to the latest one).
const [myInterview, setMyInterview] = useState(null);
const feedbackSubmittedRef = useRef(false);

const rescheduleInterviewApiUrl = process.env.REACT_APP_RESCHEDULE_INTERVIEW_API;

const { log } = useActivityLogger(recruiterEmail, userRole);
const logTarget = {
  targetId:   candidateId,
  targetName: candidateData?.candidatename,
  jdId,
};

  const updatecandidateFieldsApiUrl = process.env.REACT_APP_UPDATE_CANDIDATE_FIELDS_API;
  const sendemailApiUrl = process.env.REACT_APP_SEND_EMAIL_API;
  const testFlag = process.env.TEST_FLAG;

  const toggleFeedback = () => setIsFeedbackOpen(!isFeedbackOpen);

  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [subjectiveQuestions, setSubjectiveQuestions] = useState([]);
  const [codingQuestions, setCodingQuestions] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchUserQuestions = async (candidateID) => {
    if (!candidateID) {
      setSnackbarMessage('Candidate profile is not loaded yet.');
      setSnackbarOpen(true);
      return;
    }
    setQuestionsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_GET_CANDIDATE_SCREENING_URL}/${candidateID}`);
      setMcqQuestions(response.data.mcq_questions || []);
      setSubjectiveQuestions(response.data.subjective_questions || []);  // ← was missing
      setCodingQuestions(response.data.programming_questions || []);    
      setOpen(true);
    } catch (error) {
      console.error("Error fetching questions", error);
      setSnackbarMessage(error.response?.data?.error || error.response?.data?.message || 'Failed to load screening questions.');
      setSnackbarOpen(true);
    } finally { setQuestionsLoading(false); }
  };

  const [profiles, setProfiles] = useState([]);
  const [errorMessages, setErrorMessages] = useState({});
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [actionType, setActionType] = useState('');

  const update_candiadte_status = process.env.REACT_APP__UPDATE_CANDIDATE_STATUS_API;
  const get_candidate_profiles = process.env.REACT_APP_GET_CANDIDATE_PROFILES_API;
  const candidateProfileApi = `${get_candidate_profiles}/${candidateId}`;
  const updateCandidateStatusApi = `${update_candiadte_status}/${candidateId}`;
  const getpdfApiUrl = process.env.REACT_APP_GET_PDF_API;
  const [loadingMap, setLoadingMap] = useState({});

  // Single source for candidate data: one GET feeds both candidateData (display)
  // and candidateProfile (action buttons). Full-page loader only on first load.
  const loadCandidate = async () => {
    if (!candidateId) { setIsLoading(false); return; }
    const firstLoad = loadedIdRef.current !== candidateId;
    if (firstLoad) setIsLoading(true); else setRefreshing(true);
    try {
      const response = await axios.get(candidateProfileApi);
      const candidate = response.data?.candidate || null;
      setCandidateData(candidate);
      setCandidateProfile(candidate);
      const rawInterviews = candidate?.interviewid;
      const interviewList = Array.isArray(rawInterviews) ? rawInterviews : (rawInterviews ? [rawInterviews] : []);
      const latest = interviewList.length > 0 ? interviewList[interviewList.length - 1] : null;
      setLatestInterviewId(latest?._id || null);
      setLatestInterview(latest);   // ← full populated interview doc, includes .interviewer array
      const me = String(recruiterEmail || '').toLowerCase();
      const mine = me
        ? [...interviewList].reverse().find((iv) => Array.isArray(iv?.interviewer)
            && iv.interviewer.some((e) => String(e || '').toLowerCase() === me))
        : null;
      setMyInterview(mine || latest);
      setError(null);
      loadedIdRef.current = candidateId;
    } catch (err) {
      if (firstLoad) setError(err);
      else { setSnackbarMessage('Failed to refresh candidate data.'); setSnackbarOpen(true); }
    } finally { setIsLoading(false); setRefreshing(false); }
  };

  useEffect(() => { loadCandidate(); }, [candidateId, actionTrigger]);
  // Any status change (actionTrigger) invalidates the cached status history.
  useEffect(() => { setHistoryStale(true); }, [candidateId, actionTrigger]);

  const handleResumedisplay = async (resumePaths, jdId) => {
    if (resumeLoading) return;
        if (!resumePaths) {
      setSnackbarMessage('No resume on file for this candidate.');
      setSnackbarOpen(true);
      return;
    }
    setResumeLoading(true);
    try {
      const pathsArray = Array.isArray(resumePaths) ? resumePaths.map(obj => obj.path) : [resumePaths];
           if (pathsArray.length === 0) {
        setSnackbarMessage('No resume on file for this candidate.');
        setSnackbarOpen(true);
        return;
      }
      for (const resumePath of pathsArray) {
        const response = await axios.get(`${getpdfApiUrl}?fileName=${encodeURIComponent(resumePath)}&jdId=${encodeURIComponent(jdId ?? '')}`, { responseType: 'arraybuffer' });
        if (response.status === 200) {
          const contentType = response.headers['content-type'];
          const resumeBlob = new Blob([response.data], { type: contentType });
          const resumeUrl = URL.createObjectURL(resumeBlob);
          if (contentType === 'application/pdf') window.open(resumeUrl, '_blank');
          else if (contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') window.location.href = resumeUrl;
          else { setSnackbarMessage('Unsupported file type.'); setSnackbarOpen(true); }
        } else { setSnackbarMessage('Failed to fetch resume.'); setSnackbarOpen(true); }
      }
    } catch { setSnackbarMessage('Failed to fetch resumes.'); setSnackbarOpen(true); }
    finally { setResumeLoading(false); }
  };

  const handleFeedbackChange = (event) => setFeedback(event.target.value);

  useEffect(() => {
    if (dialogType === "edit" && selectedCandidateId) setEditingData(selectedCandidateId);
  }, [dialogType, selectedCandidateId, actionTrigger]);

  const handleUnhold = (candidateId, userRole) => handleUpdateStatus(candidateId, 'unhold', 'Unhold', userRole);

  const handleFieldChange = (field, value) => {
    setEditingData((prevData) => ({ ...prevData, rowId: prevData.rowId, [field]: value }));
  };

  const handleEditClick = (candidateProfile) => {
     if (!candidateProfile) return;
    let extractedCountryCode = "";
    let extractedPhone = candidateProfile.phone || "";
    if (extractedPhone) {
      countryOptions.forEach((country) => {
        if (extractedPhone.startsWith(country.value)) {
          extractedCountryCode = country.value;
          extractedPhone = extractedPhone.replace(country.value, "").trim();
        }
      });
    }
    // Prefill only from the current candidate — no merge with a previous candidate's edits.
    setEditingData({
      rowId: candidateProfile._id ?? null,
      candidatename: candidateProfile.candidatename ?? '',
      candidateEmail: candidateProfile.candidateEmail ?? '',
      phone: extractedPhone ?? '',
      countryCode: extractedCountryCode ?? '',
      totalExperience: candidateProfile.totalExperience ?? '',
      currentCTC: candidateProfile.currentCTC ?? '',
      expectedCTC: candidateProfile.expectedCTC ?? '',
      noticePeriod: candidateProfile.noticePeriod ?? '',
      presentLocation: candidateProfile.presentLocation ?? '',
      willingToRelocate: candidateProfile.willingToRelocate ?? '',
      preferredLocation: candidateProfile.preferredLocation ?? '',
    });
    setEditError('');
    setOpenDialog(true);
  };

  const handleEditCandidateData = async (rowId) => {
    if (!editingData.candidatename || !editingData.candidateEmail || !editingData.phone) {
      setSnackbarMessage('Candidate name, email, and phone are required.');
      setSnackbarOpen(true); return;
    }
    setEditError('');
    setLoading(true);
    try {
      const response = await axios.put(`${updatecandidateFieldsApiUrl}/${rowId}`, {
        candidatename: editingData.candidatename,
        candidateEmail: editingData.candidateEmail,
        phone: `${editingData.countryCode || ""}${editingData.phone || ""}`,
        totalExperience: editingData.totalExperience,
        currentCTC: editingData.currentCTC,
        expectedCTC: editingData.expectedCTC,
        noticePeriod: editingData.noticePeriod,
        presentLocation: editingData.presentLocation,
        willingToRelocate: editingData.willingToRelocate,
        preferredLocation: editingData.preferredLocation,
      });
      log('EDIT_CANDIDATE', {
        ...logTarget,
        metadata: {
          fieldsUpdated: [
            'candidatename', 'candidateEmail', 'phone', 'totalExperience',
            'currentCTC', 'expectedCTC', 'noticePeriod',
            'presentLocation', 'willingToRelocate', 'preferredLocation'
          ].filter(f => editingData[f] !== undefined && editingData[f] !== ''),
        }
      });
      setSnackbarMessage(response.data.message);
      setEditingData({ ...editingData, value: '' });
      setSnackbarOpen(true);
      setActionTrigger(prev => !prev);
      setTimeout(() => { setLoading(false); setOpenDialog(false); }, 100);
    } catch (err) {
      // Shown inside the edit dialog — never replaces the page.
      setEditError(err.response?.data?.error || err.response?.data?.message || 'Error updating candidate. Please try again.');
      setLoading(false);
    }
  };

  const handleButtonClick = (action, index) => {
    setActionType(action);
    if (action === 'screening' || action === 'scheduling' || action === 'un_hold') {
      handleSubmit(action, index);
    } else if (action === 'select' || action === 'decline') {
      updateCandidateStatus(action);
    } else {
      if (action === 'edit') { setDialogType("edit"); handleEditClick(candidateProfile); }
      else {
        // Don't carry feedback typed for a different action (e.g. Reject text into Accept).
        if (action !== actionType) setFeedback('');
        setDialogType("provide_feedback"); setErrorMessages({});
      }
      setOpenDialog(true);
    }
  };

  const updateCandidateStatus = async (action) => {
    if (loading) return;
           if (!candidateProfile?._id) {
      setSnackbarMessage('Candidate profile is not loaded yet.');
      setSnackbarOpen(true);
      return;
    }
    setLoading(true);
    try {
      const data = { action, candidateId: candidateProfile._id, userRole };
      await axios.post(updateCandidateStatusApi, data);
      setCandidateProfile((prevProfile) => ({
        ...prevProfile,
        overallStatus: action === 'select' ? 'Selected' : 'Declined',
      }));
      setSnackbarMessage(`Status updated to ${action === 'select' ? 'Selected' : 'Declined'}.`);
      setSnackbarOpen(true);
      setActionTrigger(prev => !prev);
    } catch (err) {
      setSnackbarMessage(err.response?.data?.error || "Failed to update status."); setSnackbarOpen(true);
    } finally { setLoading(false); }
  };

  const handleUpdateStatus = async (candidateId, action, feedback, userRole) => {
         if (!candidateId || loadingMap[candidateId]) return;
    setLoadingMap((prev) => ({ ...prev, [candidateId]: true }));
    try {
      const data = { action, feedback, userRole };
      const response = await axios.post(updateCandidateStatusApi, data);
      const newStatus = response.data?.candidateProfile?.overallStatus;
      if (newStatus) {
        setProfiles((prev) => prev.map((p) => p._id === candidateId ? { ...p, overallStatus: newStatus } : p));
        setCandidateProfile((prev) => prev?._id === candidateId ? { ...prev, overallStatus: newStatus } : prev);
      }
      setActionTrigger(prev => !prev);
    } catch (err) {
      console.error('Error updating status', err);
      setSnackbarMessage(err.response?.data?.error || err.response?.data?.message || 'Failed to un-hold candidate.');
      setSnackbarOpen(true);
    }
    finally { setLoadingMap((prev) => ({ ...prev, [candidateId]: false })); }
  };

  const handleOpenReschedule = () => {
  if (!latestInterviewId) {
    setSnackbarMessage('No scheduled interview found for this candidate.');
    setSnackbarOpen(true);
    return;
  }
  const now = new Date();
  setRescheduleDate(now.toISOString().split('T')[0]);
  setRescheduleTime(now.toTimeString().slice(0, 5));

  // ✅ prefill from the interview's own interviewer array — no string-mashing
  const existingInterviewers = Array.isArray(latestInterview?.interviewer)
    ? latestInterview.interviewer.filter(Boolean)
    : [];
  setRescheduleInterviewers(existingInterviewers);

  setRescheduleReason('');
  log('RESCHEDULE_INTERVIEW_INITIATED', {
    ...logTarget,
    metadata: { interviewId: latestInterviewId, existingInterviewers },
  });
  setShowRescheduleDialog(true);
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const handleRescheduleSubmit = async () => {
  if (!rescheduleDate || !rescheduleTime) {
    setSnackbarMessage('Please select both date and time.');
    setSnackbarOpen(true);
    return;
  }

  const invalidEmails = rescheduleInterviewers.filter(e => !EMAIL_RE.test(e));
  if (rescheduleInterviewers.length === 0 || invalidEmails.length > 0) {
    setSnackbarMessage(
      invalidEmails.length > 0
        ? `Invalid interviewer email(s): ${invalidEmails.join(', ')}`
        : 'At least one interviewer email is required.'
    );
    setSnackbarOpen(true);
    return;
  }

  setRescheduling(true);
  try {
    const payload = {
      interview_time: new Date(`${rescheduleDate}T${rescheduleTime}`).toISOString(),
      interviewerEmails: rescheduleInterviewers,
      changedBy: recruiterEmail,
      role: userRole,
      reason: rescheduleReason,
    };
    const response = await axios.put(`${rescheduleInterviewApiUrl}/${latestInterviewId}`, payload);
    log('RESCHEDULE_INTERVIEW_CONFIRMED', {
      ...logTarget,
      metadata: {
        interviewId: latestInterviewId,
        newTime: payload.interview_time,
        interviewers: rescheduleInterviewers,
        reason: rescheduleReason,
      },
    });
    setSnackbarMessage(response.data?.message || 'Interview rescheduled successfully.');
    setSnackbarOpen(true);
    setShowRescheduleDialog(false);
    setActionTrigger(prev => !prev);
  } catch (err) {
    console.error('Reschedule failed:', err.message);
    setSnackbarMessage('Failed to reschedule interview. Please try again.');
    setSnackbarOpen(true);
  } finally {
    setRescheduling(false);
  }
};

  const handleSubmit = async (action) => {
    if (loading) return;
    if (!candidateProfile || !candidateProfile._id) {
      setSnackbarMessage("Candidate profile is missing."); setSnackbarOpen(true); return;
    }
    if (action === 'accept' || action === 'reject' || action === 'on_hold') {
      const wordCount = feedback.trim() ? feedback.trim().split(/\s+/).length : 0;
      if (wordCount < 2 || wordCount > 50) {
        setErrorMessages((prev) => ({ ...prev, [candidateProfile._id]: 'Reason must be 2–50 words.' }));
        return;
      } else setErrorMessages((prev) => ({ ...prev, [candidateProfile._id]: '' }));
    }
    setLoading(true);
    let succeeded = false;
    try {
      const data = { action: actionType, feedback: feedback.trim(), userRole };
      const response = await axios.post(updateCandidateStatusApi, data);
      const updatedProfile = response.data.candidateProfile;
      if (updatedProfile) setCandidateProfile(updatedProfile);
      setCandidateProfile((prev) => ({
        ...prev,
        ResumeStatus: updatedProfile?.overallStatus,
        [userRole === 'hiringManager' ? 'hiringManagerFeedback' : 'recruiterFeedback']: feedback,
      }));
      if (actionType === 'accept') {
        const emailPayload = { candidateEmail: response.data.recipientEmails, subject: response.data.subject, body: response.data.message };
        try { if (testFlag === 'false') await axios.post(sendemailApiUrl, emailPayload); }
        catch { setSnackbarMessage('Failed to send email.'); setSnackbarOpen(true); }
      }
      succeeded = true;
      setActionTrigger(prev => !prev); 
    } catch (error) {
      // Keep the dialog (and the typed feedback) open; show the error inline.
      const msg = error.response?.status === 400
        ? (error.response.data.error || error.response.data.message || "Bad request.")
        : (error.response?.data?.error || "An error occurred.");
      setErrorMessages((prev) => ({ ...prev, [candidateProfile._id]: msg }));
    } finally {
      setLoading(false);
      if (succeeded) { setOpenDialog(false); setFeedback(''); setActionType(''); }
    }
  };

  const buttonConfigs = [
    { status: 'Accepted',    label: 'Accept',     action: 'accept',     className: 'accept-btn' },
    { status: 'Rejected',    label: 'Reject',      action: 'reject',     className: 'reject-btn' },
    { status: 'Selected',    label: 'Select',      action: 'select',     className: 'accept-btn' },
    { status: 'Declined',    label: 'Reject',      action: 'decline',    className: 'reject-btn' },
    { status: 'Screening',   label: (i) => loadingMap[i] ? 'Sending…' : 'Send Screening', action: 'screening', className: 'allbuttons-btn',
      onClick: (candidateEmail, jdId, index) => handleScreening(candidateData.candidateEmail, candidateData.jddetails_id, candidateId, candidateData.candidatename, jobDescription, salaryRange, candidateData.jobRole, companyName, recruiterEmail, index,  experience, 'screening'),
    },
    { status: 'Rescreening', label: (i) => loadingMap[i] ? 'Sending…' : 'Resend Screening', action: 'rescreening', className: 'allbuttons-btn',
      onClick: (candidateEmail, jdId, index) => handleScreening(candidateData.candidateEmail, candidateData.jddetails_id, candidateId, candidateData.candidatename, jobDescription, salaryRange, candidateData.jobRole, companyName, recruiterEmail, index, experience, 'rescreening'),
    },
    { status: 'Reschedule', label: 'Reschedule Interview', action: 'reschedule', className: 'on-hold-btn', onClick: () => handleOpenReschedule() },
    { status: 'Un Hold',   label: 'Un Hold',            action: 'un_hold',   className: 'allbuttons-btn', onClick: (candidateId) => handleUnhold(candidateId, userRole) },
    { status: 'Scheduling',label: 'Schedule Interview',  action: 'scheduling', className: 'allbuttons-btn', onClick: (candidateEmail, jdId) => handleScheduleClick(candidateEmail, jdId, candidateProfile?.candidateID) },
    { status: 'On Hold',   label: 'Hold',                action: 'on_hold',   className: 'on-hold-btn' },
  ];

  const renderButtonsForHM = () => {
    if (!candidateProfile?.overallStatus) return null;
    const sharedActions = ['accept', 'reject', 'on_hold'];
    const buttonFilter = {
      'NewCandidate': [], 'Recruiter_Accepted': sharedActions, 'Recruiter_Rejected': [], 'Recruiter_Hold': [],
      // Backend can't reject from HiringManager_Accepted (returns 400) — only hold is offered.
      'HiringManager_Accepted': ['on_hold'], 'HiringManager_Rejected': ['accept'],
      'HiringManager_Hold': ['un_hold'], 'ScreeningTest_Sent': [], 'ScreeningTest_Passed': ['on_hold'],
      'ScreeningTest_Failed': [], 'OnHold_TestPassed': ['un_hold'], 'Interview_Scheduled': [],
      'Interviewer_Selected': ['select', 'decline'],
    };
    const visibleActions = buttonFilter[candidateProfile.overallStatus] || [];
    return buttonConfigs.filter(({ action }) => visibleActions.includes(action)).map(({ label, action, className, onClick }) => (
      <ActionBtn key={action} className={className} disabled={actionInFlight}
        onClick={() => { if (onClick && action === 'un_hold') onClick(candidateProfile._id); else handleButtonClick(action); }}>
        {action === 'un_hold' && loadingMap[candidateProfile._id] ? 'Processing…' : label}
      </ActionBtn>
    ));
  };

  const renderButtonsforRecruiter = () => {
    if (!candidateProfile?.overallStatus) return null;
    const buttonFilter = {
      'NewCandidate': ['accept', 'reject', 'on_hold'], 'Recruiter_Accepted': ['reject', 'on_hold'],
      'Recruiter_Rejected': ['accept'], 'Recruiter_Hold': ['un_hold'],
      'HiringManager_Accepted': ['screening'], 'HiringManager_Rejected': [], 'HiringManager_Hold': [],
      'ScreeningTest_Sent': ['rescreening', 'on_hold'], 'ScreeningTest_Resent': ['on_hold'],
      'ScreeningTest_Passed': ['scheduling', 'on_hold', 'reject'],
      'ReScreeningTest_Passed': ['scheduling', 'on_hold', 'reject'],
      'ScreeningTest_Failed': ['rescreening', 'scheduling', 'on_hold'],
      'OnHold_TestPassed': ['un_hold'], 'OnHold_Screening': ['un_hold'], 'OnHold_ReScreening': ['un_hold'],
      'Interview_Scheduled': [], 'Round2_Suggested': ['scheduling', 'on_hold', 'reject'],
      'Interview_Scheduled': ['reschedule'],  
      'Interview_Rescheduled': ['reschedule'],  
      'Round2_Scheduled': ['reschedule'], 
      'Selected': [], 'Rejected': [], 'TestPassed_Rejected': [], 'ReTestPassed_Rejected': [],
    };
    const visibleActions = buttonFilter[candidateProfile.overallStatus] || [];
    return buttonConfigs.filter(({ action }) => visibleActions.includes(action)).map(({ label, action, className, onClick }, index) => (
      <ActionBtn key={action} className={className} disabled={actionInFlight || !!loadingMap[index]}
        onClick={() => {
          if (onClick) {
            if (action === 'screening' || action === 'rescreening') onClick(candidateEmail, jdId, index);
            else if (action === 'scheduling') onClick(candidateData?.candidateEmail ?? candidateEmail, candidateData?.jddetails_id ?? jdId);
            else if (action === 'un_hold') onClick(candidateProfile._id);
            else if (action === 'reschedule') onClick();
          } else handleButtonClick(action, index);
        }}>
        {action === 'un_hold' && loadingMap[candidateProfile._id] ? 'Processing…' : (typeof label === 'function' ? label(index) : label)}
      </ActionBtn>
    ));
  };

  const [showScheduleInterface, setShowScheduleInterface] = useState(false);
  const [scheduleCandidateEmail, setScheduleCandidateEmail] = useState('');
  const [scheduleJdId, setScheduleJdId] = useState('');
  const [candidateID, setCandidateID] = useState('');
  const [successMap, setSuccessMap] = useState({});
  const screeningtestEmailApiUrl = process.env.REACT_APP_SCREENING_TEST_EMAIL_API;
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedCandidateEmail, setSelectedCandidateEmail] = useState('');
  const handleCloseFeedbackForm = () => {
    setShowFeedbackForm(false);
    // Reload so the status / interview feedback reflect what was just submitted.
    if (feedbackSubmittedRef.current) { feedbackSubmittedRef.current = false; setActionTrigger(prev => !prev); }
  };

  const handleScreening = async (candidateEmail, jdId, candidateId, candidateName, jobDescription, salaryRange, jobRole, companyName, recruiterEmail, index,  experience, action) => {
    try {
      console.log("candidate id in send screening",candidateId)
      if (!candidateId ) { alert("Missing required info."); return; }
      if (loadingMap[index]) return;
      setLoadingMap((prev) => ({ ...prev, [index]: true }));
      const emailResponse = await axios.post(screeningtestEmailApiUrl, {
        email: candidateEmail, candidateId, candidateName, jobDescription, recruiterEmail,
        salaryRange, jobRole, companyName, jdId, action,
      });
      if (emailResponse.status === 200) {
        log(action === 'rescreening' ? 'RESEND_SCREENING_TEST' : 'SEND_SCREENING_TEST', {
          targetId:   candidateId,
          targetName: candidateName,
          jdId,
          metadata: { candidateEmail, jobRole, action }
        });
        setSuccessMap((prev) => ({ ...prev, [index]: true }));
        setSnackbarMessage(action === 'rescreening' ? "Screening Test resent successfully" : "Screening Test sent successfully");
        setSnackbarOpen(true);
        setCandidateProfile((prev) => ({ ...prev, overallStatus: action === 'rescreening' ? 'ScreeningTest_Resent' : 'ScreeningTest_Sent' }));
        setActionTrigger(prev => !prev);
      }
    } catch { setSnackbarMessage("Error sending email."); setSnackbarOpen(true); }
    finally { setLoadingMap((prev) => ({ ...prev, [index]: false })); }
  };

  const handleScheduleClick = (candidateEmail, jdId, candidateID) => {
    log('SCHEDULE_INTERVIEW_INITIATED', {
      targetId:   candidateId,
      targetName: candidateData?.candidatename,
      jdId,
      metadata:   { candidateEmail }
    });
    setScheduleCandidateEmail(candidateEmail); setScheduleJdId(jdId);
    setCandidateID(candidateID);
    setShowScheduleInterface(true);
  };

  const handleScheduleBack = () => {
    setShowScheduleInterface(false);
    setActionTrigger(prev => !prev);
  };

  const renderActionLinkInterviewer = (overallStatus, userType, candidateEmail, candidateId) => {
    if (userType !== 'interviewer') return null;
    const allowedStatuses = ['Interview_Scheduled', 'Round2_Scheduled'];
    if (!allowedStatuses.includes(overallStatus)) return null;
    const completed = String(myInterview?.interviewStatus || '').toLowerCase() === 'completed';
    return (
      <ActionBtn className="allbuttons-btn" disabled={!myInterview?._id || completed || refreshing}
        title={!myInterview?._id ? 'No interview found for this candidate' : completed ? 'Feedback already submitted' : undefined}
        onClick={() => {
        if (!myInterview?._id) return;
        feedbackSubmittedRef.current = false;
        setSelectedCandidateEmail(candidateEmail);
        setCandidateID(candidateId);
        setActionType('provideFeedback');
        setShowFeedbackForm(true);
      }}>
        {completed ? 'Feedback Submitted' : 'Provide Feedback'}
      </ActionBtn>
    );
  };

  const fetchStatusHistory = async () => {
    if (historyLoading) return;
    if (!historyStale) { setShowHistory(true); return; }
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_GET_CANDIDATE_PROFILES_API}/${candidateId}/status-history`
      );
      setStatusHistory(res.data.history || []);
      setHistoryStale(false);
      setShowHistory(true);
    } catch (e) {
      console.error('Failed to fetch status history', e);
      setSnackbarMessage(e.response?.data?.error || 'Failed to load status history.');
      setSnackbarOpen(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const statusColor = (status) => {
    if (!status) return '#8B7DB5';
    if (['Selected', 'ScreeningTest_Passed', 'ReScreeningTest_Passed',
         'Recruiter_Accepted', 'HiringManager_Accepted', 'Interviewer_Selected']
        .includes(status)) return '#2D7D46';
    if (['Recruiter_Rejected', 'HiringManager_Rejected', 'ScreeningTest_Failed',
         'ReScreeningTest_Failed', 'Rejected', 'Round1_Rejected', 'TestPassed_Rejected',
         'ReTestPassed_Rejected'].includes(status)) return '#B3261E';
    if (status.includes('Hold'))   return '#E65100';
    if (status.includes('Scheduled')) return '#1565C0';
    return '#6750A4';
  };
  
  const roleLabel = (role) => ({
    recruiter: '👤 Recruiter', hiringManager: '🏢 Hiring Manager',
    candidate: '📋 Candidate', system: '⚙️ System',
  }[role] || role);

  useEffect(() => {
    if (candidateData) {
      // Assuming your backend returns isActive field, if not it defaults to true
      setIsActive(candidateData.isActive !== false); 
    }
  }, [candidateData]);

  // 3. Add the Toggle Function
const handleToggleActiveStatus = async () => {
  setIsToggling(true);
  try {
    const newStatus = !isActive;
    const response = await axios.put(`${updatecandidateFieldsApiUrl}/${candidateId}`, {
      isActive: newStatus
    });
    
    if (response.status === 200) {
      log(newStatus ? 'MARK_CANDIDATE_ACTIVE' : 'MARK_CANDIDATE_INACTIVE', {
        ...logTarget,
        metadata: { previousState: isActive, newState: newStatus }
      });
      setIsActive(newStatus);
      setSnackbarMessage(`Candidate marked as ${newStatus ? 'Active' : 'Inactive'}`);
      setSnackbarOpen(true);
      // Trigger a data refresh if necessary
      setActionTrigger(prev => !prev);
    }
  } catch (error) {
    setSnackbarMessage('Error updating active status.');
    setSnackbarOpen(true);
  } finally {
    setIsToggling(false);
  }
};

  // Any status-changing action in flight → disable the action buttons.
  const actionInFlight = loading || rescheduling || isToggling || Object.values(loadingMap).some(Boolean);

  if (isLoading) return (
    <ThemeProvider theme={recruiterTheme}>
    <PageWrapper $embedded={embedded}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', py: 5 }}>
        <Typography sx={{ fontFamily: FONT_BODY, color: COLORS.muted, fontSize: TYPE.body, lineHeight: 1.5 }}>Loading candidate data…</Typography>
      </Box>
    </PageWrapper>
    </ThemeProvider>
  );
  if (error || !candidateData) return (
    <ThemeProvider theme={recruiterTheme}>
    <PageWrapper $embedded={embedded}>
      <HeaderBar>
        <HeaderLeft>
          <BackBtn onClick={handleBack} aria-label="Back"><ArrowBackIcon /></BackBtn>
          <PageTitle>Candidate Details</PageTitle>
        </HeaderLeft>
      </HeaderBar>
      <Box>
        <InfoCard sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center', py: '40px !important' }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: error ? COLORS.error : COLORS.muted }}>
            {error
              ? `Failed to load candidate data: ${error.response?.data?.error || error.response?.data?.message || error.message}`
              : 'No candidate data found.'}
          </Typography>
          {candidateId && (
            <ActionBtn className="allbuttons-btn" onClick={() => { setError(null); loadCandidate(); }}>
              Retry
            </ActionBtn>
          )}
        </InfoCard>
      </Box>
    </PageWrapper>
    </ThemeProvider>
  );

  const score = candidateData.match_score || 0;
  const chip = getStatusChip(candidateData.overallStatus);
  const effectiveJdId = jdId || candidateData.jdID;

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={recruiterTheme}>
    <PageWrapper $embedded={embedded}>
      {showFeedbackForm ? (
        <FeedbackForm showFeedbackForm={showFeedbackForm} onCloseFeedbackForm={handleCloseFeedbackForm}
          selectedCandidateEmail={selectedCandidateEmail} selectedJdID={candidateData.jdID}
          selectedJdId={candidateData.jddetails_id}
          selectedInterviewId={(userRole === 'interviewer' ? myInterview?._id : null) || latestInterviewId}
          interviewStatus={(userRole === 'interviewer' ? myInterview : latestInterview)?.interviewStatus}
          onSubmitted={() => { feedbackSubmittedRef.current = true; }}
          selectedJobRole={candidateData.jobRole} selectedCandidateID={candidateData.candidateID}
          selectedCandidateName={candidateData.candidatename} setActiveTab={setActiveTab} />
      ) : showScheduleInterface ? (
        <SchedulingPage candidateEmail={scheduleCandidateEmail} jdId={candidateData.jddetails_id}
          recruiterEmail={recruiterEmail}
          candidateID={candidateProfile?.candidateID} candidateName={candidateData.candidatename}
          jobTitle={candidateData.jobRole} companyName={companyName}
          onBack={() => setShowScheduleInterface(false)} onScheduled={handleScheduleBack} />
      ) : (
        <>
          {/* Header */}
          <HeaderBar>
            <HeaderLeft>
              <BackBtn onClick={handleBack} aria-label="Back"><ArrowBackIcon /></BackBtn>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <PageTitle title={candidateData.candidatename || undefined}>
                    {candidateData.candidatename}
                  </PageTitle>
                  <Chip
                    label={isActive ? "ACTIVE" : "INACTIVE"}
                    size="small"
                    sx={{
                      height: 24,
                      flexShrink: 0,
                      fontFamily: FONT_BODY,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      '& .MuiChip-label': { fontSize: TYPE.overline, px: 1.5 },
                      bgcolor: isActive ? '#E6F4EA' : '#FDECEA',
                      color: isActive ? '#1E7E34' : '#B3261E',
                      border: `1px solid ${isActive ? '#C3E6CB' : '#F5C6CB'}`,
                    }}
                  />
                </Box>
                <PageSubtitle>
                  <Box component="span" sx={{ fontWeight: 600, color: '#6750A4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {candidateData.jobRole}
                  </Box>
                  {companyName && (
                    <>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#C4B0E8', flexShrink: 0 }} />
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {companyName}
                      </Box>
                    </>
                  )}
                </PageSubtitle>
              </Box>
            </HeaderLeft>
          </HeaderBar>

          {/* ── Stat Cards ── */}
          <StatsRow>
            {/* Match Score card */}
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatLbl>Match Score</StatLbl>
              <StatNum>{score}%</StatNum>
              <ScoreBar><ScoreFill val={score} /></ScoreBar>
              <StatIconWrap><TrendingUpIcon /></StatIconWrap>
            </StatCard>

            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatLbl>Candidate ID</StatLbl>
              <StatNum $text title={candidateData.candidateID || undefined}>{candidateData.candidateID}</StatNum>
              <StatIconWrap><PersonIcon /></StatIconWrap>
            </StatCard>

 
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatLbl>Screening Score</StatLbl>
              <StatNum>{candidateData.ScreeningTestScore != null ? `${candidateData.ScreeningTestScore}%` : '—'}</StatNum>
              <StatIconWrap><AssessmentIcon /></StatIconWrap>
            </StatCard>

            {/* Status card */}
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatLbl>Status</StatLbl>
              <StatusChip bg={chip.bg} fg={chip.fg} title={formatStatus(candidateData.overallStatus || '')}
                style={{ maxWidth: 'calc(100% - 40px)' }}>
                <StatusDot color={chip.dot} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatStatus(candidateData.overallStatus || '')}
                </Box>
              </StatusChip>
              <StatIconWrap><FlagIcon /></StatIconWrap>
            </StatCard>
          </StatsRow>


          {/* ── Action Strip ── */}
          <ActionStrip>
            <ActionLabel>Actions</ActionLabel>
            {refreshing && (
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.muted }}>Refreshing…</Typography>
            )}

            <ActionBtn className="allbuttons-btn" disabled={!candidateProfile || resumeLoading}
              onClick={() => handleResumedisplay(candidateProfile?.resumeUrl, effectiveJdId)}>
              {resumeLoading ? 'Opening…' : 'View Resume'}
            </ActionBtn>
            <ActionBtn className="allbuttons-btn" disabled={!candidateProfile?.candidateID || questionsLoading}
              onClick={() => fetchUserQuestions(candidateProfile?.candidateID)}>
             {questionsLoading ? 'Loading…' : 'View Questions'}
            </ActionBtn>

            {userRole === 'hiringManager' && !readOnly && renderButtonsForHM()}

            {userRole === 'recruiter' && !readOnly && (
              <>
                {renderButtonsforRecruiter()}
                <ActionBtn className="allbuttons-btn" disabled={!candidateProfile || actionInFlight} onClick={() => {
                  handleButtonClick('edit');
                }}>
                  Edit
                </ActionBtn>
              </>
            )}
            {userRole === 'recruiter' && !readOnly && (
        <ActionBtn 
          className={isActive ? "reject-btn" : "accept-btn"} 
          onClick={handleToggleActiveStatus}
          disabled={isToggling}
          style={{ minWidth: 120 }}
        >
          {isToggling ? 'Processing...' : (isActive ? 'Mark Inactive' : 'Mark Active')}
        </ActionBtn>
      )}
      <ActionBtn className="allbuttons-btn" onClick={fetchStatusHistory} disabled={historyLoading}>
  {historyLoading ? 'Loading…' : 'Status History'}
</ActionBtn>
      <ActionBtn className="allbuttons-btn" onClick={() => navigate(`/proctoring/${candidateId}`)}>
        Camera Proctoring
      </ActionBtn>

{/* After ContentGrid closing tag — add the timeline panel */}


            {userRole === 'interviewer' && !readOnly &&
              renderActionLinkInterviewer(candidateData.overallStatus || overallStatus, userRole, candidateData.candidateEmail || candidateEmail, candidateData._id || candidateId)}
          </ActionStrip>

          {/* ── Main Content Grid ── */}
          <ContentGrid>
            {/* Left: Info Card */}
            <InfoCard>
              <CardTitle><PersonIcon sx={{ fontSize: 20, color: '#6750A4' }} /> Candidate Information</CardTitle>

              {[
                ['JD ID',            candidateData.jdID],
                ['Candidate ID',     candidateData.candidateID],
                ['Name',             candidateData.candidatename],
                ['Email',            candidateData.candidateEmail],
                ['Phone',            candidateData.phone],
                ['Job Role',         candidateData.jobRole],
                ['Experience',       candidateData.totalExperience ? `${candidateData.totalExperience} years` : null],
                ['Current CTC',      candidateData.currentCTC ? `${candidateData.currentCTC} LPA` : null],
                ['Expected CTC',     candidateData.expectedCTC ? `${candidateData.expectedCTC} LPA` : null],
                ['Notice Period',    candidateData.noticePeriod ? `${candidateData.noticePeriod} days` : null],
                ['Present Location', candidateData.presentLocation],
                ['Relocate',         candidateData.willingToRelocate],
                ['Preferred Loc.',   candidateData.preferredLocation],
              ].filter(([, v]) => v).map(([k, v]) => (
                <InfoRow key={k}>
                  <InfoKey>{k}</InfoKey>
                  <InfoVal>{v}</InfoVal>
                </InfoRow>
              ))}

              {/* Screening score */}
              {candidateData.ScreeningTestScore != null && (
                <InfoRow>
                  <InfoKey>Screening Score</InfoKey>
                  <InfoVal>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 80, height: 6, borderRadius: `${RADIUS.pill}px`, background: '#F0EAF9', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', borderRadius: `${RADIUS.pill}px`, background: candidateData.ScreeningTestScore >= 60 ? '#2D7D46' : '#E65100', width: `${candidateData.ScreeningTestScore}%` }} />
                      </Box>
                      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{candidateData.ScreeningTestScore}%</span>
                    </Box>
                  </InfoVal>
                </InfoRow>
              )}

              {/* Feedback fields */}
              {candidateData.recruiterFeedback && (
                <InfoRow>
                  <InfoKey>Recruiter Feedback</InfoKey>
                  <InfoVal style={{ fontSize: TYPE.small, color: '#6B5B9A' }}>{candidateData.recruiterFeedback}</InfoVal>
                </InfoRow>
              )}
              {candidateData.hiringManagerFeedback && (
                <InfoRow>
                  <InfoKey>HM Feedback</InfoKey>
                  <InfoVal style={{ fontSize: TYPE.small, color: '#6B5B9A' }}>{candidateData.hiringManagerFeedback}</InfoVal>
                </InfoRow>
              )}

              {/* Interview Feedback toggle */}
              {candidateData.interviewFeedback && Object.values(candidateData.interviewFeedback).some(v => v) && (
                <Box sx={{ mt: 2 }}>
                  <FeedbackToggle onClick={toggleFeedback}>
                    <FeedbackIcon sx={{ fontSize: 16 }} />
                    Interview Feedback
                    {isFeedbackOpen ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  </FeedbackToggle>
                  {isFeedbackOpen && candidateData.interviewFeedback?.length > 0 && (
                    candidateData.interviewFeedback.map((fb, i) => (
                      <FeedbackTable key={i} component={Paper}>
                        <Table size="small">
                          <TableBody>
                            {[
                              ['Programming Skills', fb.programmingRating],
                              ['Technical Skills', fb.technicalSkills],
                              ['Communication', fb.communicationSkills],
                              ['Problem Solving', fb.problemSolvingSkills],
                              ['Overall Rating', fb.reviewRating],
                              ['Detailed Feedback', fb.feedbackText],
                            ].map(([label, value]) => (
                              <TableRow key={label}>
                                <TableCell sx={{ fontWeight: 600, color: COLORS.muted, fontSize: TYPE.caption, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '12px 16px', borderColor: '#F0EAF9', width: '40%' }}>
                                  {label}
                                </TableCell>
                                <TableCell sx={{ fontSize: TYPE.small, lineHeight: 1.5, color: COLORS.ink, padding: '12px 16px', borderColor: '#F0EAF9' }}>{value}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </FeedbackTable>
                    ))
                  )}
                </Box>
              )}

              {latestInterview?.interviewStatus && (
                <InfoRow>
                  <InfoKey>Interview Status</InfoKey>
                  <InfoVal>{latestInterview.interviewStatus}</InfoVal>
                </InfoRow>
              )}
            </InfoCard>

            {/* Right: Summary Card */}
            <SummaryCard>
              <CardTitle><DescriptionIcon sx={{ fontSize: 20, color: '#6750A4' }} /> Resume Summary</CardTitle>
              <ul style={{ paddingLeft: 16, margin: 0 }}>

                  {(candidateData.summaries || "")
                  .replace(/\*/g, "")
                  .split(/(?<!\d)\.\s+|\n|;/)
                  .filter(point => point.trim() !== "")
                  .map((point, index) => (
                    <BulletItem key={index}>{point.trim()}</BulletItem>
                  ))}
              </ul>
            </SummaryCard>
          </ContentGrid>

          {/* ── Dialogs ── */}
          <Dialog open={openDialog} onClose={() => { if (!loading) setOpenDialog(false); }}
            PaperProps={{ sx: { width: '42%', minWidth: 'min(560px, calc(100vw - 32px))', maxWidth: 'none', ...dialogPaperSx } }}>
            {dialogType === "edit" ? (
              <>
                <DialogTitleStyled>Edit Candidate Details</DialogTitleStyled>
                <DialogContent sx={dialogContentSx}>
                  <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid item xs={12}><TextField label="Candidate Name" fullWidth value={editingData?.candidatename || ""} onChange={(e) => handleFieldChange("candidatename", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={12}><TextField label="Candidate Email" fullWidth value={editingData?.candidateEmail || ""} onChange={(e) => handleFieldChange("candidateEmail", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel shrink>Code</InputLabel>
                        <Select value={editingData?.countryCode || ""} onChange={(e) => handleFieldChange("countryCode", e.target.value)} displayEmpty sx={{ borderRadius: `${RADIUS.sm}px` }}>
                          <MenuItem value=""><em>Select</em></MenuItem>
                          {countryOptions.map((c) => (<MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={8}><TextField label="Phone" fullWidth value={editingData?.phone || ""} onChange={(e) => handleFieldChange("phone", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={6}><TextField label="Current CTC" fullWidth type="number" value={editingData?.currentCTC || ""} onChange={(e) => handleFieldChange("currentCTC", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={6}><TextField label="Expected CTC" fullWidth type="number" value={editingData?.expectedCTC || ""} onChange={(e) => handleFieldChange("expectedCTC", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={12}><TextField label="Total Experience" fullWidth type="number" value={editingData?.totalExperience || ""} onChange={(e) => handleFieldChange("totalExperience", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={12}><TextField label="Notice Period (days)" fullWidth value={editingData?.noticePeriod || ""} onChange={(e) => handleFieldChange("noticePeriod", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={12}><TextField label="Present Location" fullWidth value={editingData?.presentLocation || ""} onChange={(e) => handleFieldChange("presentLocation", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={12}><TextField label="Preferred Location" fullWidth value={editingData?.preferredLocation || ""} onChange={(e) => handleFieldChange("preferredLocation", e.target.value)} sx={inputSx} /></Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Willing to Relocate</InputLabel>
                        <Select value={editingData?.willingToRelocate || ""} label="Willing to Relocate" onChange={(e) => handleFieldChange("willingToRelocate", e.target.value)} sx={{ borderRadius: `${RADIUS.sm}px` }}>
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  {editError && (
                    <Typography sx={{ color: '#B3261E', fontSize: TYPE.caption, lineHeight: 1.5, mt: 1.5, fontFamily: FONT_BODY }}>
                      {editError}
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                  <Button onClick={() => setOpenDialog(false)} sx={cancelBtnSx}>Cancel</Button>
                  <Button onClick={() => handleEditCandidateData(candidateId)} disabled={loading}
                    sx={primaryBtnSx}>
                    {loading ? 'Updating…' : 'Save Changes'}
                  </Button>
                </DialogActions>
              </>
            ) : (
              <>
                <DialogTitleStyled>
                  {({ accept: 'Accept', reject: 'Reject', on_hold: 'Hold' })[actionType]
                    ? `${({ accept: 'Accept', reject: 'Reject', on_hold: 'Hold' })[actionType]} — Provide Feedback`
                    : 'Provide Feedback'}
                </DialogTitleStyled>
                <DialogContent sx={dialogContentSx}>
                  <TextField sx={inputSx}
                    variant="outlined" placeholder="Enter your feedback (2–50 words)…"
                    fullWidth required value={feedback} onChange={handleFeedbackChange} multiline rows={8} />
                  {candidateProfile && errorMessages[candidateProfile._id] && (
                    <Typography sx={{ color: '#B3261E', fontSize: TYPE.caption, lineHeight: 1.5, mt: 1, fontFamily: FONT_BODY }}>
                      {errorMessages[candidateProfile._id]}
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                  <Button onClick={() => setOpenDialog(false)} sx={cancelBtnSx}>Cancel</Button>
                  <Button onClick={() => handleSubmit(actionType)} disabled={loading}
                    sx={primaryBtnSx}>
                    {loading ? 'Submitting…' : 'Submit'}
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Status History Dialog */}
<Dialog
  open={showHistory}
  onClose={() => setShowHistory(false)}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      ...dialogPaperSx,
      boxShadow: '0 20px 60px rgba(79,55,139,0.2)',
      maxHeight: '85vh',
    }
  }}
>
  <Box sx={dialogHeaderSx}>
    <Typography sx={dialogHeaderTitleSx}>
      <TimelineIcon sx={{ color: '#6750A4', fontSize: 20 }} />
      Candidate Journey — {statusHistory.length} events
    </Typography>
    <IconButton onClick={() => setShowHistory(false)} size="small" aria-label="Close" sx={closeBtnSx}>
      ✕
    </IconButton>
  </Box>

  <DialogContent sx={dialogContentSx}>
    <Box sx={{ position: 'relative', pl: '32px' }}>
      {/* Vertical line */}
      <Box sx={{
        position: 'absolute', left: '10px', top: 0, bottom: 0,
        width: '2px', background: 'linear-gradient(to bottom, #6750A4, #EDE7F6)',
        borderRadius: '2px',
      }} />

      {statusHistory.map((entry, i) => {
        const color = statusColor(entry.status);
        const isLast = i === statusHistory.length - 1;
        return (
          <Box key={i} sx={{ position: 'relative', mb: isLast ? 0 : 3 }}>
            {/* Dot */}
            <Box sx={{
              position: 'absolute', left: '-28px', top: '16px',
              width: '14px', height: '14px', borderRadius: '50%',
              background: color, border: '2px solid #fff',
              boxShadow: `0 0 0 2px ${color}33`,
            }} />

            {/* Card */}
            <Box sx={{
              background: isLast ? '#F8F4FF' : '#FAFAFA',
              border: `1.5px solid ${isLast ? '#D0BCFF' : '#EDE7F6'}`,
              borderRadius: '12px', padding: '12px 16px',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                {/* Status badge */}
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 12px', borderRadius: `${RADIUS.pill}px`,
                  background: `${color}18`, border: `1px solid ${color}44`,
                  fontFamily: FONT_BODY, fontWeight: 700, whiteSpace: 'nowrap',
                  fontSize: TYPE.overline, letterSpacing: '0.06em', color,
                }}>
                  {entry.status}
                </Box>
                {/* Timestamp */}
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption,
                  color: '#8B7DB5', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {new Date(entry.timestamp).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </Typography>
              </Box>

              {/* Meta row */}
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                {entry.changedBy && (
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, lineHeight: 1.5,
                    color: '#6B5B9A', fontWeight: 600 }}>
                    {roleLabel(entry.role)} — {entry.changedBy}
                  </Typography>
                )}
                {entry.note && (
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, lineHeight: 1.5,
                    color: '#49454F', fontStyle: 'italic' }}>
                    "{entry.note}"
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}

      {statusHistory.length === 0 && (
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5,
          color: '#8B7DB5', textAlign: 'center', py: 5 }}>
          No history recorded yet.
        </Typography>
      )}
    </Box>
  </DialogContent>
</Dialog>

<Dialog open={showRescheduleDialog} onClose={() => setShowRescheduleDialog(false)}
  PaperProps={{ sx: { ...dialogPaperSx, minWidth: 'min(440px, calc(100vw - 32px))' } }}>
  <DialogTitleStyled>Reschedule Interview</DialogTitleStyled>
  <DialogContent sx={dialogContentSx}>
    <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: '#8B7DB5', mb: 2 }}>
      {candidateData?.candidatename} — {candidateData?.jobRole}
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
      <TextField
        label="New Date" type="date" InputLabelProps={{ shrink: true }}
        value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)}
        inputProps={{ min: new Date().toISOString().split('T')[0] }}
        sx={inputSx}
      />
      <TextField
        label="New Time" type="time" InputLabelProps={{ shrink: true }}
        value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)}
        sx={inputSx}
      />
    </Box>
    <Autocomplete
      multiple freeSolo
      options={[]}
      value={rescheduleInterviewers}
      onChange={(e, value) => setRescheduleInterviewers(value.map(v => v.trim()).filter(Boolean))}
      renderInput={(params) => (
        <TextField {...params} label="Interviewer email(s)"
          placeholder="Type an email and press Enter"
          sx={{ mb: 2, ...inputSx }} />
      )}
    />
    <TextField
      label="Reason for reschedule (optional)"
      fullWidth multiline rows={2}
      value={rescheduleReason}
      onChange={(e) => setRescheduleReason(e.target.value)}
      sx={inputSx}
    />
  </DialogContent>
  <DialogActions sx={dialogActionsSx}>
    <Button onClick={() => setShowRescheduleDialog(false)}
      sx={cancelBtnSx}>
      Cancel
    </Button>
    <Button onClick={handleRescheduleSubmit} disabled={rescheduling}
      sx={primaryBtnSx}>
      {rescheduling ? 'Saving…' : 'Save Changes'}
    </Button>
  </DialogActions>
</Dialog>

          <NotificationDialog open={snackbarOpen} message={snackbarMessage} onClose={() => setSnackbarOpen(false)} />
          <Dialog
  open={open}
  onClose={() => setOpen(false)}
  maxWidth="lg"
  fullWidth
  PaperProps={{
    sx: {
      ...dialogPaperSx,
      boxShadow: '0 20px 60px rgba(79,55,139,0.2)',
      maxHeight: '90vh',
    }
  }}
>
  <Box sx={dialogHeaderSx}>
    <Typography sx={dialogHeaderTitleSx}>
      Test Results — {candidateData?.candidatename}
    </Typography>
    <IconButton onClick={() => setOpen(false)} size="small" aria-label="Close" sx={closeBtnSx}>
      ✕
    </IconButton>
  </Box>
  <DialogContent sx={dialogContentSx}>
    <CandidateTestResults
      mcqQuestions={mcqQuestions}
      subjectiveQuestions={subjectiveQuestions}
      codingQuestions={codingQuestions}
      candidateName={candidateData?.candidatename}
    />
  </DialogContent>
</Dialog>
        </>
      )}
    </PageWrapper>
    </ThemeProvider>
  );
};

export default CandidateData;
